import * as React from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import { numero, raioDoPonto } from '../Consultas.graficos'
import type { PontoMapa } from '../Dashboards.scopes'
import { COR } from '../Operacao.graficos'

// Fundo do OpenStreetMap (sem chave de API), em tons de cinza para ficar parecido com o mapa do Power BI. O tráfego
// do portal é pequeno, dentro da política de uso dos tiles do OSM; os créditos são obrigatórios.
const FUNDO = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
const CREDITOS = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'

/** O Brasil inteiro na tela. */
const BRASIL: L.LatLngBoundsExpression = [
    [-33.8, -74],
    [5.3, -34.7]
]

const escapar = (texto: string) =>
    texto.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c))

export type MapaConsultasProps = {
    pontos: PontoMapa[]
    /** Clique num município: o painel filtra por ele. */
    onMunicipio: (ponto: PontoMapa) => void
    altura?: number
}

/**
 * Um círculo por município, com a área proporcional aos CNPJs consultados nele (no Power BI, o tamanho era a
 * contagem distinta de CNPJs). Passar o mouse mostra os números; clicar filtra o painel pelo município.
 */
export function MapaConsultas({ pontos, onMunicipio, altura = 440 }: MapaConsultasProps) {
    const ref = React.useRef<HTMLDivElement>(null)
    const mapa = React.useRef<L.Map>()
    const camada = React.useRef<L.LayerGroup>()
    const aoClicar = React.useRef(onMunicipio)
    aoClicar.current = onMunicipio

    React.useEffect(() => {
        if (!ref.current) return
        // Em canvas: milhares de círculos em SVG deixariam o zoom lento.
        const instancia = L.map(ref.current, { preferCanvas: true, zoomSnap: 0.25, scrollWheelZoom: false })
        instancia.fitBounds(BRASIL)
        L.tileLayer(FUNDO, { attribution: CREDITOS, maxZoom: 18, className: 'mapa-consultas-fundo' }).addTo(instancia)
        camada.current = L.layerGroup().addTo(instancia)
        mapa.current = instancia
        const observer = new ResizeObserver(() => instancia.invalidateSize())
        observer.observe(ref.current)
        return () => {
            observer.disconnect()
            instancia.remove()
            mapa.current = undefined
            camada.current = undefined
        }
    }, [])

    React.useEffect(() => {
        const grupo = camada.current
        if (!grupo) return
        grupo.clearLayers()
        const maximo = pontos.reduce((m, p) => Math.max(m, p.cnpjs), 0)
        // Os maiores primeiro, para os pequenos ficarem por cima e continuarem clicáveis.
        for (const ponto of [...pontos].sort((a, b) => b.cnpjs - a.cnpjs)) {
            L.circleMarker([ponto.latitude, ponto.longitude], {
                radius: raioDoPonto(ponto.cnpjs, maximo),
                color: COR.serie1,
                weight: 1,
                opacity: 0.9,
                fillColor: COR.serie1,
                fillOpacity: 0.45
            })
                .bindTooltip(
                    `<b>${escapar(ponto.municipio)}/${escapar(ponto.uf)}</b><br/>${numero(
                        ponto.consultas
                    )} consultas · ${numero(ponto.cnpjs)} CNPJs`,
                    { direction: 'top' }
                )
                .on('click', () => aoClicar.current(ponto))
                .addTo(grupo)
        }
    }, [pontos])

    return (
        <div>
            <style>{'.mapa-consultas-fundo { filter: grayscale(1) brightness(1.04) contrast(0.92); }'}</style>
            <div
                ref={ref}
                role="region"
                aria-label="Mapa das consultas por município"
                style={{ width: '100%', height: altura, borderRadius: 10, zIndex: 0 }}
            />
        </div>
    )
}

export default MapaConsultas
